import React from "react";
import {Alert, Card, Spinner, Table} from "react-bootstrap";
import HelpTooltip from "../HelpTooltip.jsx";
import useStatisticsData from "./useStatisticsData.js";
import {ABC_COLORS, formatQuantity, OTHER_GROUP_COLOR} from "./format.js";
import {RecommendationBadge} from "./inventoryStrategyShared.jsx";

const KINDS = {
    offenders: {
        endpoint: "/statistics/inventory-strategy/top-offenders",
        title: "Zu viel auf Lager",
        help: "Artikel mit dem größten Überbestand in Paletten, absteigend sortiert. Nur Artikel mit bekannter Palettengröße.",
        amountField: "excess_units",
        amountLabel: "Überbestand",
        emptyLabel: "Kein Artikel mit Überbestand gefunden.",
    },
    understocked: {
        endpoint: "/statistics/inventory-strategy/understocked",
        title: "Unter Meldebestand",
        help: "Artikel unter dem Meldebestand, nach Umsatzrelevanz sortiert (A- vor B- vor C-Artikeln).",
        amountField: "shortfall_units",
        amountLabel: "Fehlmenge",
        emptyLabel: "Kein Artikel unter dem Meldebestand gefunden.",
    },
};

/**
 * Handlungsliste (#4 im Auftrag): zwei parametrisierte Ausprägungen dieser
 * einen Komponente - "Zu viel auf Lager" (top-offenders) und "Unter
 * Meldebestand" (understocked). Zeigt je Artikel Menge, Einstufung und die
 * fachliche Begründung aus recommendation_reason.
 *
 * Die Begründung steht bewusst NICHT in einer eigenen, schmalen
 * Tabellenspalte - zwei Handlungslisten nebeneinander lassen dafür keinen
 * Platz, der Text lief rechts aus dem Kasten heraus. Stattdessen folgt sie
 * als eigene, über die volle Kastenbreite laufende Zeile unter der
 * Artikelzeile - dadurch bleibt sie unabhängig von der Fensterbreite immer
 * vollständig lesbar, ohne horizontales Scrollen der Tabelle zu brauchen.
 *
 * Nur die fünf dringendsten Fälle je Liste (siehe `limit`) - die
 * vollständige, filterbare Übersicht liefert die Mengenempfehlungs-Tabelle
 * oberhalb dieser Listen.
 */
export default function InventoryActionList({kind, params, limit = 5}) {
    const config = KINDS[kind];
    const requestParams = {...params, limit};
    const {data, loading, error} = useStatisticsData(config.endpoint, requestParams);
    const rows = data?.rows || [];

    return (
        <Card className="shadow-sm h-100">
            <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <span className="d-flex flex-column">
                    <span className="d-inline-flex align-items-center gap-1 fw-semibold">
                        {config.title}
                        <HelpTooltip placement="right" size={0.8}>{config.help}</HelpTooltip>
                    </span>
                    <span className="small text-muted">Die {limit} dringendsten Fälle</span>
                </span>
                {loading && (
                    <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Wird geladen…</span>
                    </Spinner>
                )}
            </Card.Header>
            <Card.Body className="p-0">
                {error && <Alert variant="danger" className="m-3 mb-0">{error}</Alert>}
                <div style={{overflowX: "auto"}}>
                    <Table hover size="sm" className="align-middle mb-0">
                        <thead>
                            <tr>
                                <th>Artikel</th>
                                <th>ABC</th>
                                <th className="text-end">{config.amountLabel}</th>
                                <th>Einstufung</th>
                            </tr>
                        </thead>
                        <tbody style={{opacity: loading ? 0.6 : 1, transition: "opacity 120ms ease"}}>
                            {!loading && rows.length === 0 && (
                                <tr>
                                    <td colSpan={4} className="text-center text-muted py-4">
                                        {config.emptyLabel}
                                    </td>
                                </tr>
                            )}
                            {rows.map((row) => (
                                <React.Fragment key={row.article_number}>
                                    <tr>
                                        <td>
                                            <div className="font-monospace small">{row.article_number}</div>
                                            <div>{row.name || "–"}</div>
                                        </td>
                                        <td>
                                            {row.abc_class ? (
                                                <span className="badge" style={{backgroundColor: ABC_COLORS[row.abc_class] || OTHER_GROUP_COLOR}}>
                                                    {row.abc_class}
                                                </span>
                                            ) : "–"}
                                        </td>
                                        <td className="text-end fw-semibold">{formatQuantity(row[config.amountField])} Stk.</td>
                                        <td><RecommendationBadge value={row.recommendation} reason={null} /></td>
                                    </tr>
                                    {row.recommendation_reason && (
                                        <tr>
                                            <td colSpan={4} className="small text-muted pt-0 pb-2">
                                                {row.recommendation_reason}
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
        </Card>
    );
}
